import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "hooks";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Alert, Box, Button, Grid, Paper, Snackbar, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import {
    useLazyGetDriverAndVehicleDetailQuery,
    useLazySearchDriversForTransferQuery,
    useTransferVehicleShipmentsMutation,
} from "store/services/driverApi";
import {
    useLazyGetShipmentDetailByVehicleIdQuery,
} from "store/services/shipmentLoadingApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

const INITIAL_STATE = { driver: null, vehicle: null };

export const DriverTransferScreen = () => {
    const debounceRef = useRef(null);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const { loggedInUser, viewEntity } = useAuth();
    const driverIds = loggedInUser?.typeRef;

    const [fetchDrivers, { data: driverOptions, isLoading: isLoadingDrivers }] = useLazySearchDriversForTransferQuery();
    const [fetchDriverVehicle, { data, isLoading }] = useLazyGetDriverAndVehicleDetailQuery();
    const [fetchShipmentDetails, { data: shipmentData, isLoading: isLoadingShipments }] =
        useLazyGetShipmentDetailByVehicleIdQuery();
    const [transferShipments, { isLoading: isTransferring }] = useTransferVehicleShipmentsMutation();

    const { control, reset } = useForm({ defaultValues: INITIAL_STATE, mode: "onChange" });

    const selectedDriver = useWatch({ control, name: "driver" });
    const selectedVehicle = useWatch({ control, name: "vehicle" });

    useEffect(() => {
        if (driverIds) fetchDriverVehicle(driverIds);
    }, [driverIds, fetchDriverVehicle]);

    useEffect(() => {
        if (data?.assigned_vehicle_ids?.length) {
            reset({ vehicle: data.assigned_vehicle_ids[0] });
        }
    }, [data, reset]);

    useEffect(() => {
        if (selectedVehicle?._id) {
            fetchShipmentDetails({ vehicleId: selectedVehicle._id });
        }
    }, [selectedVehicle?._id, fetchShipmentDetails]);

    const groupedByLoading = useMemo(() => {
        if (!shipmentData?.data?.shipments) return [];
        return shipmentData.data.shipments.reduce((acc, item) => {
            const existing = acc.find((g) => g.loadingId === item.loadingId);
            if (existing) {
                existing.shipmentIds.push(item.shipmentId);
            } else {
                acc.push({ loadingId: item.loadingId, shipmentIds: [item.shipmentId] });
            }
            return acc;
        }, []);
    }, [shipmentData]);

    const handleTransfer = async () => {
        if (!selectedDriver?._id || !shipmentData?.data?.shipments?.length) return;

        const loadingIds = [...new Set(shipmentData.data.shipments.map((s) => s.loadingId))];

        const result = await transferShipments({
            driverId: selectedDriver._id,
            loadingIds,
            vehicleId: selectedVehicle._id,
            vehicleVehicleId: selectedVehicle.vehicle_id,
            vehicleRegistrationNumber: selectedVehicle.registration_number,
        });

        if (result?.data?.success) {
            setToast({
                open: true,
                severity: "success",
                message: `Transfer request sent to ${selectedDriver.name}. Waiting for approval.`,
            });
        } else {
            setToast({
                open: true,
                severity: "error",
                message: result?.error?.data?.message || "Transfer failed. Please try again.",
            });
        }
    };

    return (
        <Grid container spacing={3}>

            <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="flex-start">

                    {/* Vehicle Selector */}
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="vehicle"
                            control={control}
                            rules={{ required: "Please select a vehicle." }}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Vehicle"
                                    value={value ?? null}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) =>
                                        option ? `${option.vehicle_id} - ${option.registration_number}` : ""
                                    }
                                    isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                    options={data?.assigned_vehicle_ids || []}
                                    loading={isLoading}
                                    loadingText="Loading Vehicles..."
                                    helperText={error?.message}
                                    error={!!error}
                                />
                            )}
                        />
                    </Box>

                    {/* Driver Search */}
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="driver"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Driver"
                                    value={value ?? null}
                                    onChange={(_, data) => onChange(data)}
                                    onInputChange={(_, inputValue, reason) => {
                                        if (reason === "reset") return;
                                        clearTimeout(debounceRef.current);
                                        debounceRef.current = setTimeout(() => {
                                            if (inputValue.trim()) fetchDrivers({
                                                q: inputValue.trim(),
                                                excludeId: driverIds,
                                            });
                                        }, 300);
                                    }}
                                    getOptionLabel={(option) =>
                                        option ? `${option.name} (${option.driverId})` : ""
                                    }
                                    isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                    options={driverOptions || []}
                                    loading={isLoadingDrivers}
                                    loadingText="Searching drivers..."
                                />
                            )}
                        />
                    </Box>

                    {/* Transfer Button */}
                    <Box sx={{ pt: 0.5 }}>
                        <Button
                            variant="contained"
                            onClick={handleTransfer}
                            disabled={!selectedDriver?._id || !shipmentData?.data?.shipments?.length || isTransferring}
                            sx={{ whiteSpace: "nowrap", height: 40 }}
                        >
                            {isTransferring ? "Transferring..." : "Transfer Vehicle Shipments"}
                        </Button>
                    </Box>

                </Stack>
            </Grid>

            {/* Summary */}
            {shipmentData?.data && (
                <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                        {[
                            { label: "Total Loadings", value: shipmentData.data.totalLoadings },
                            { label: "Total Shipments", value: shipmentData.data.totalShipments },
                            { label: "Total Weight", value: `${shipmentData.data.totalWeight} kg` },
                        ].map(({ label, value }) => (
                            <Paper
                                key={label}
                                variant="outlined"
                                sx={{ px: 2, py: 1, minWidth: 120, textAlign: "center", borderRadius: 0, borderColor: "divider", bgcolor: "background.paper" }}
                            >
                                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">{value}</Typography>
                            </Paper>
                        ))}
                    </Stack>
                </Grid>
            )}

            {/* Grouped Table */}
            {isLoadingShipments ? (
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Loading shipments...</Typography>
                </Grid>
            ) : (
                groupedByLoading.length > 0 && (
                    <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, borderColor: "divider", bgcolor: "background.paper" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        <TableCell sx={{ fontWeight: 700, width: 160, color: "text.primary", borderColor: "divider" }}>Loading ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "text.primary", borderColor: "divider" }}>Shipment IDs</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedByLoading.map(({ loadingId, shipmentIds }) => (
                                        <TableRow key={loadingId} sx={{ verticalAlign: "top", "&:last-child td": { borderBottom: 0 } }}>
                                            <TableCell sx={{ borderColor: "divider" }}>
                                                <Typography variant="caption" fontWeight={600} color="primary" sx={{ fontFamily: "monospace" }}>
                                                    {loadingId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderColor: "divider" }}>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {shipmentIds.map((shipmentId) => (
                                                        <Box key={shipmentId} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 0, px: 1, py: 0.25, bgcolor: "action.hover" }}>
                                                            <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.72rem", color: "text.primary" }}>
                                                                {shipmentId}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                )
            )}

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
                    {toast.message}
                </Alert>
            </Snackbar>

        </Grid>
    );
};